import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Chip,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  Avatar,
  ListItemAvatar,
  Container,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import { enhancePrompt, deletePinnedPromptForAgent } from "../api/agentApi";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Description,
  Image as ImageIcon,
  ArrowBack as ArrowBackIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import {
  getAgentRunResultFromUrl,
  createPinnedPromptForAgent,
  listPinnedPromptsForAgent,
  listAllAgentRuns,
  getAgentRunArtifacts,
  downloadArtifactFile,
  API_BASE,
  getAuthToken,
  getAgentRunPrompt,
  getAgentRunResult,
} from "../api/agentApi";
import { marked } from "marked";
import { getAgent, runAgent } from "../api/agentApi";
import { useParams, useNavigate } from "react-router-dom";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PromptEnhancerDialog from "./AgentDetail/PromptEnhancerDialog.tsx";
import { AgentPinnedPrompt } from "../types.ts";
import ArtifactsAndPinnedPromptsSideBar from "./AgentDetail/ArtifactsAndPinnedPromptsSideBar.tsx";
import HistorySidebar from "./AgentDetail/HistorySidebar.tsx";
import PinPromptDropdown from "./AgentDetail/PinPromptDropdown.tsx";
import AgentResponseDisplay from "./AgentDetail/AgentResponseDisplay.tsx";
import ThinkingIndicator from "./AgentDetail/ThinkingIndicator.tsx";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import StopIcon from '@mui/icons-material/Stop';
import { parsePrompt } from "./UIUtils.ts";

// Types
interface AgentRunResult {
  status: string;
  message?: string;
  output?: string;
  agent_version?: number;
  run_number?: number;
}

const AgentRunPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  // History Sidebar
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [agentRuns, setAgentRuns] = useState<any[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [totalHistoryRuns, setTotalHistoryRuns] = useState(0);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  const [promptText, setPromptText] = useState("");
  const [runStatus, setRunStatus] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [finalResult, setFinalResult] = useState("");
  const [thinkingDots, setThinkingDots] = useState(0);
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [agent, setAgent] = useState<any>(null);
  const [typewriterSpeed, setTypewriterSpeed] = useState(20);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enhancedSuggestion, setEnhancedSuggestion] = useState("");
  const [pinnedPrompts, setPinnedPrompts] = useState<AgentPinnedPrompt[]>([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [hasRunTriggered, setHasRunTriggered] = useState(false);
  const [isReturningToProgress, setIsReturningToProgress] = useState(false);
  const pollTimeoutRef = React.useRef<any>(null);
  const dotsIntervalRef = React.useRef<any>(null);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [liveChatArtifacts, setLiveChatArtifacts] = useState<any[]>([]); // Store live chat artifacts
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [selectedRunHistory, setSelectedRunHistory] = useState<any>(null);
  const [historicalPrompt, setHistoricalPrompt] = useState("");
  const [historicalResult, setHistoricalResult] = useState("");
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [feedbackState, setFeedbackState] = useState<"up" | "down" | null>(
    null,
  );

  // ******************************************************************
  // Voice input state
  // ******************************************************************
  const [inputValue, setInputValue] = useState("");
  const [submittedPrompt, setSubmittedPrompt] = useState(""); // Store the last submitted prompt for display
  const [isPromptExpanded, setIsPromptExpanded] = useState(false); // Track if prompt bubble is expanded
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const POLL_INTERVAL_BASE = 5000; // ms
  const PROMPT_RESPONSE_WIDTH = "70%";

  useEffect(() => {
    // Check for browser support and handle the prefix
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error("Speech recognition not supported in this browser.");
    } else {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false; // Stops when you stop talking
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }
  }, []);

  const handleMicClick = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setIsListening(true);
      recognitionRef.current?.start();
    }
  };

  // ******************************************************************
  // ******************************************************************

  // Fetch agent data on mount
  useEffect(() => {
    let poller: any;
    let isUnmounted = false;
    const fetchAgent = async () => {
      try {
        const data = await getAgent(agentId);
        if (!isUnmounted) {
          setAgent(data);
          setError("");
          // Only poll every 5s if status is 'creating' or 'building'
          if (data.status === "creating" || data.status === "building") {
            poller = setTimeout(fetchAgent, POLL_INTERVAL_BASE);
          }
          // No polling for other statuses
        }
      } catch {
        if (!isUnmounted) setError("Failed to load agent details.");
      }
    };
    fetchAgent();
    return () => {
      isUnmounted = true;
      if (poller) clearTimeout(poller);
    };
  }, [agentId]);
  // Use agent object directly (API now returns JSON)
  const agentCard = agent;

  React.useEffect(() => {
    setInputValue("")
  }, []);

  // Fetch pinned prompts
  useEffect(() => {
    if (!agentId) return;
    const fetchPinnedPrompts = async () => {
      try {
        const data = await listPinnedPromptsForAgent(agentId);
        setPinnedPrompts(data);
      } catch (err) {
        console.error("Failed to fetch pinned prompts", err);
        setPinnedPrompts([]);
      }
    };
    fetchPinnedPrompts();
  }, [agentId, runsRefreshToken]);

  // Load historical run data when a run is selected from history
  useEffect(() => {
    if (!selectedRunHistory || !agentId) return;

    const loadHistoricalRun = async () => {
      // Save current live chat artifacts before loading historical run
      setLiveChatArtifacts(artifacts);

      setIsLoadingHistorical(true);
      try {
        // Fetch prompt, result, and artifacts for the selected run
        const [promptData, resultData, artifactsData] = await Promise.all([
          getAgentRunPrompt(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
          getAgentRunResult(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
          getAgentRunArtifacts(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
        ]);

        setHistoricalPrompt(parsePrompt(promptData?.prompt || ""));
        setHistoricalResult(resultData?.output || "");

        // Transform artifacts to ensure they have all required fields
        const transformedArtifacts = (artifactsData?.artifacts || []).map(
          (art: any, index: number) => ({
            id: art.id || `artifact-${selectedRunHistory.run_number}-${index}`,
            name: art.name || art.filename || "Unknown",
            filename: art.filename || art.name || "Unknown",
            type: art.type || "",
            url: art.url || "",
            agent_id: selectedRunHistory.agent_id,
            agent_version: selectedRunHistory.agent_version,
            run_number: selectedRunHistory.run_number,
          }),
        );
        setArtifacts(transformedArtifacts);
      } catch (err) {
        console.error("Failed to load historical run:", err);
        setHistoricalPrompt("Failed to load prompt");
        setHistoricalResult("Failed to load result");
      } finally {
        setIsLoadingHistorical(false);
      }
    };

    loadHistoricalRun();
  }, [selectedRunHistory, agentId, runsRefreshToken]);

  // ****************************
  // Call the enhance prompt API
  // ****************************
  const enhancePromptApi = async (): Promise<string> => {
    try {
      const response = await enhancePrompt({
        role: agentCard?.role,
        description: agentCard?.description,
        input_prompt: inputValue,
        tools: agentCard?.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      return response.enhanced_prompt;
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      throw new Error("Failed to enhance prompt. Please try again.");
    }
  };

  // Fetch Agent Runs for History (initial load)
  useEffect(() => {
    if (!agentId) return;
    const fetchRuns = async () => {
      try {
        const response = await listAllAgentRuns(agentId, {
          page: 1,
          page_size: historyPageSize,
          version: null,
        });
        setAgentRuns(response.runs || []);
        setTotalHistoryRuns(response.total || 0);
        setHistoryPage(1);
        setHasMoreHistory((response.runs || []).length < (response.total || 0));
      } catch (err) {
        console.error("Failed to fetch history", err);
      }
    };
    fetchRuns();
  }, [agentId, runsRefreshToken, historyPageSize]);

  // Load more history runs
  const handleLoadMoreHistory = async () => {
    if (!agentId || isLoadingMoreHistory || !hasMoreHistory) return;

    setIsLoadingMoreHistory(true);
    try {
      const nextPage = historyPage + 1;
      const response = await listAllAgentRuns(agentId, {
        page: nextPage,
        page_size: historyPageSize,
        version: null,
      });

      const newRuns = response.runs || [];
      setAgentRuns((prev) => [...prev, ...newRuns]);
      setHistoryPage(nextPage);

      // Check if there are more runs to load
      const totalLoaded = agentRuns.length + newRuns.length;
      setHasMoreHistory(totalLoaded < (response.total || 0));
    } catch (err) {
      console.error("Failed to load more history", err);
    } finally {
      setIsLoadingMoreHistory(false);
    }
  };

  const handleCopyPrompt = (text: string) => {
    setInputValue(text);
  };

  const handleClearInput = () => {
    setInputValue("");
  };

  const handleDeletePrompt = async (promptId: any) => {
    setError("");
    if (!window.confirm(`Delete prompt '${promptId}'? This cannot be undone.`))
      return;
    setDeleting(promptId);
    try {
      await deletePinnedPromptForAgent(agentId, promptId);
      setPinnedPrompts(pinnedPrompts.filter((p: any) => p.id !== promptId));
    } catch (e) {
      setError("Failed to delete prompt.");
    } finally {
      setDeleting("");
    }
  };

  const handleArtifactDownload = async (artifact: any) => {
    try {
      // Use the proper API endpoint like AgentRuns does
      if (
        !artifact.agent_id ||
        !artifact.agent_version ||
        !artifact.run_number ||
        !artifact.filename
      ) {
        console.error("Artifact missing required fields:", artifact);
        return;
      }

      console.log("Downloading artifact:", {
        agent_id: artifact.agent_id,
        agent_version: artifact.agent_version,
        run_number: artifact.run_number,
        filename: artifact.filename,
      });

      const response = await downloadArtifactFile(
        artifact.agent_id,
        artifact.agent_version,
        artifact.run_number,
        artifact.filename,
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch: ${response?.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", artifact.filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Download failed for ${artifact.filename}.`);
    }
  };

  const regeneratePrompt = async () => {
    if (!agent) return;
    setIsEnhancing(true);
    try {
      const result = await enhancePrompt({
        role: agent.role,
        description: agent.description,
        input_prompt: inputValue,
        tools: agent.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      setEnhancedSuggestion(result.enhanced_prompt);
    } catch (err) {
      console.error("Failed to regenerate:", err);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleEnhanceClick = async () => {
    setIsModalOpen(true);
    await regeneratePrompt();
  };

  const handleAcceptEnhancement = (finalPrompt: string) => {
    setInputValue(finalPrompt)
    setIsModalOpen(false); // Close modal
  };

  const handleRejectEnhancement = () => {
    setIsModalOpen(false); // Close modal
  };

  // ******************************************************************
  // File attachment in prompt
  // ******************************************************************
  const [selectedFiles, setSelectedFiles] = useState<FileReference[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterText, setFilterText] = useState("");
  const [fileLibrary, setFileLibrary] = useState<FileReference[]>([]);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const [hasLoadedArtifacts, setHasLoadedArtifacts] = useState(false);
  const [loadedRunsCount, setLoadedRunsCount] = useState(0);
  const [totalRunsCount, setTotalRunsCount] = useState(0);
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set()); // Track all created blob URLs

  interface FileReference {
    id: string;
    name: string;
    version: string;
    runNumber: number;
    thumbnail?: string; // URL to the generated thumbnail
  }

  // Fetch all artifacts from all runs when user first types @file
  // Helper function to fetch image securely as blob (prevents token leakage in logs/history)
  const fetchSecureImage = async (url: string): Promise<string | undefined> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch image: ${response.status}`);
        return undefined;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Track this blob URL for cleanup on unmount
      blobUrlsRef.current.add(blobUrl);

      return blobUrl;
    } catch (error) {
      console.error("Failed to fetch secure image:", error);
      return undefined;
    }
  };

  const INITIAL_RUNS_LIMIT = 20;
  const LOAD_MORE_INCREMENT = 20; // Load 20 more runs when user clicks "Load More"
  const CONCURRENCY_LIMIT = 20; // Process 20 runs concurrently

  const fetchAllArtifacts = async (append: boolean = false) => {
    if (isLoadingArtifacts || !agentId) return;

    setIsLoadingArtifacts(true);
    try {
      // Calculate how many runs to fetch
      const limit = append ? LOAD_MORE_INCREMENT : INITIAL_RUNS_LIMIT;
      const offset = append ? loadedRunsCount : 0;

      // Fetch one page to get the most recent runs
      const runsResponse = await listAllAgentRuns(agentId, {
        page: Math.floor(offset / 200) + 1,
        page_size: 200,
        version: null,
      });

      const allRuns = runsResponse.runs || [];
      const totalRuns = runsResponse.total || 0;

      // Slice to only get the runs we want (most recent first)
      const runsToProcess = allRuns.slice(
        offset % 200,
        Math.min((offset % 200) + limit, allRuns.length),
      );

      setTotalRunsCount(totalRuns);
      const newLoadedCount = offset + runsToProcess.length;
      setLoadedRunsCount(newLoadedCount);
      setHasMoreRuns(newLoadedCount < totalRuns);

      // Helper function to check if file is an image
      const isImageFile = (filename: string) => {
        const imageExtensions = [
          ".png",
          ".jpg",
          ".jpeg",
          ".gif",
          ".bmp",
          ".webp",
          ".svg",
        ];
        return imageExtensions.some((ext) =>
          filename.toLowerCase().endsWith(ext),
        );
      };

      // Helper function to process a single run's artifacts
      const processRunArtifacts = async (
        run: any,
      ): Promise<FileReference[]> => {
        try {
          const response = await getAgentRunArtifacts(
            agentId,
            run.agent_version,
            run.run_number,
          );

          const artifacts = response?.artifacts || [];
          if (artifacts.length === 0) return [];

          // Fetch thumbnails concurrently for all images in this run
          const artifactPromises = artifacts.map(async (artifact: any) => {
            const thumbnail = isImageFile(artifact.filename)
              ? await fetchSecureImage(artifact.url)
              : undefined;

            return {
              id: `${run.agent_version}-${run.run_number}-${artifact.filename}`,
              name: artifact.filename,
              version: `${run.agent_version}`,
              runNumber: run.run_number,
              thumbnail: thumbnail,
            };
          });

          return await Promise.all(artifactPromises);
        } catch (err) {
          console.error(
            `Failed to fetch artifacts for run ${run.run_number}:`,
            err,
          );
          return [];
        }
      };

      // Fetch artifacts with bounded concurrency
      const newArtifacts: FileReference[] = [];

      for (let i = 0; i < runsToProcess.length; i += CONCURRENCY_LIMIT) {
        const batch = runsToProcess.slice(i, i + CONCURRENCY_LIMIT);
        const batchResults = await Promise.all(
          batch.map((run: any) => processRunArtifacts(run)),
        );

        // Flatten and add to results
        batchResults.forEach((artifacts) => {
          newArtifacts.push(...artifacts);
        });
      }

      // Append to existing or replace
      if (append) {
        setFileLibrary((prev) => [...prev, ...newArtifacts]);
      } else {
        setFileLibrary(newArtifacts);
        setHasLoadedArtifacts(true);
      }
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
      if (!append) {
        setFileLibrary([]);
        setHasLoadedArtifacts(true);
      }
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  // Load more artifacts when user clicks "Load More"
  const loadMoreArtifacts = () => {
    fetchAllArtifacts(true);
  };
  // Sync selectedFiles with promptText to prevent orphaned file references
  React.useEffect(() => {
    // Remove files from selectedFiles if their names are no longer in the prompt
    setSelectedFiles((current) =>
      current.filter((file) => inputValue.includes(file.name)),
    );
  }, [inputValue]);

  // Cleanup blob URLs ONLY on component unmount
  React.useEffect(() => {
    return () => {
      // Revoke all blob URLs to prevent memory leaks
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []); // Empty dependency array = only runs on unmount

  // Handle @file input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    setInputValue(val);

    const matches = Array.from(val.matchAll(/@file(\w*)/g));
    const match = matches.length > 0 ? matches[matches.length - 1] : null;
    if (match) {
      setFilterText(match[1] || "");
      setAnchorEl(inputRef.current);

      if (!hasLoadedArtifacts && !isLoadingArtifacts) {
        fetchAllArtifacts();
      }
    } else {
      setAnchorEl(null);
    }
  };

  const handleFileSelect = (file: FileReference) => {
    const lastTriggerIndex = inputValue.lastIndexOf("@file");
    if (lastTriggerIndex !== -1) {
      // 1. Always replace @file with the filename in the text
      const before = inputValue.substring(0, lastTriggerIndex);
      const newinputValue = `${before}${file.name} `;
      setInputValue(newinputValue);

      // 2. Track metadata - only add if not already present (prevent duplicate chips)
      // The useEffect will clean up selectedFiles automatically
      if (!selectedFiles.find((f) => f.id === file.id)) {
        setSelectedFiles((prev) => [...prev, file]);
        console.log(`Added file ${file.name} to selected files`);
      } else {
        console.log(
          `File ${file.name} already in selected files - only updating text`,
        );
      }
    }
    setAnchorEl(null);
  };
  const filteredOptions = fileLibrary.filter((f) =>
    f.name.toLowerCase().includes(filterText.toLowerCase()),
  );

  if (!agent) {
    return (
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        <Typography>Loading agent...</Typography>
      </Container>
    );
  }

  // ******************************************************************

  const OnRunCompleted = () => {
    // Clear polling timeout
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    // Clear thinking dots interval
    if (dotsIntervalRef.current) {
      clearInterval(dotsIntervalRef.current);
      dotsIntervalRef.current = null;
    }

    // Reset polling state
    setIsPolling(false);
    setIsRunning(false);
    setThinkingDots(0);
  };

  const handleRun = async () => {
    // Save the prompt before clearing
    setSubmittedPrompt(inputValue);
    setIsPromptExpanded(false); // Reset expand state for new prompt
    
    setRunStatus(null);
    setIsRunning(true);
    setIsPolling(false);
    setFinalResult("");
    setHasRunTriggered(true);
    setFeedbackState(null); // Reset feedback state on new run
    setIsReturningToProgress(false); // Reset flag for new run
    try {
      // Prepare file references if any files are selected
      const fileReferences =
        selectedFiles.length > 0
          ? selectedFiles.map((f) => ({
              id: f.id,
              name: f.name,
              version: f.version,
              runNumber: f.runNumber,
              thumbnail: f.thumbnail || null,
            }))
          : null;

      if (fileReferences) {
        console.log("Running agent with file references:", fileReferences);
      }

      // Capture the agent version before running
      const agentVersionAtRun = agent?.version;
      console.log("Agent version at run time:", agentVersionAtRun);

      // Run the agent (with optional file references)
      const result = await runAgent(
        agentId,
        JSON.stringify({ input: inputValue }) + "\n",
        openaiApiKey,
        fileReferences as any, // Pass files to runAgent - it will create runcontext.json internally
      );
      console.log("runAgent result:", result);
      console.log("Agent state:", agent);
      setRunStatus(result);
      setSnackbarOpen(true);

      // Poll for final result if result_endpoint exists
      if (result.result_endpoint) {
        console.log(
          "Starting polling for result_endpoint:",
          result.result_endpoint,
        );
        setIsPolling(true);
        setFinalResult("");
        setThinkingDots(0);

        // Animate 'Thinking...' ellipsis (0 to 3 dots)
        dotsIntervalRef.current = setInterval(() => {
          setThinkingDots((prev) => (prev + 1) % 4);
        }, 500);

        // Helper function to stop polling and cleanup
        const stopPolling = () => {
          setIsPolling(false);
          OnRunCompleted();
          if (dotsIntervalRef.current) {
            clearInterval(dotsIntervalRef.current);
            dotsIntervalRef.current = null;
          }
        };

        // Helper function to refresh agent data (updates number_of_executions)
        const refreshAgent = async () => {
          try {
            const updated = await getAgent(agentId);
            setAgent(updated);
            console.log(
              "Agent data refreshed. New execution count:",
              updated.number_of_executions,
            );
          } catch (err) {
            console.error("Failed to refresh agent data:", err);
          }
        };

        // Recursive polling function
        const pollResult = async () => {
          try {
            const data = (await getAgentRunResultFromUrl(
              result.result_endpoint,
            )) as any;
            console.log("Poll response:", data);

            // Success - stop polling and display result
            if (data.status === "success") {
              stopPolling();

              // Helper function to strip "Final Answer" heading if it exists
              const stripFinalAnswerHeading = (text: string): string => {
                // Try multiple patterns to catch different formats
                let cleaned = text;
                // Pattern 1: "Final Answer" followed by newline (with or without colon)
                cleaned = cleaned.replace(
                  /^\s*final\s+answer:?\s*[\r\n]+/im,
                  "",
                );
                // Pattern 2: "Final Answer" followed by any whitespace
                cleaned = cleaned.replace(/^\s*final\s+answer:?\s*/im, "");
                return cleaned.trim();
              };

              // Handle different output types
              if (typeof data.output === "string" && data.output.trim()) {
                const cleanedOutput = stripFinalAnswerHeading(data.output);
                setFinalResult(cleanedOutput);
                setRunsRefreshToken((prev) => prev + 1);
              } else if (data.output && typeof data.output === "object") {
                setFinalResult(JSON.stringify(data.output, null, 2));
                setRunsRefreshToken((prev) => prev + 1);
              } else {
                setFinalResult(
                  "Agent completed successfully but returned no output.",
                );
              }

              // Refresh agent data to update number_of_executions for next run
              await refreshAgent();

              // Fetch artifacts if available
              try {
                // Use captured agent version from when run started and result.run_number from the API response
                const runNumber = result.run_number;

                console.log(
                  "Fetching artifacts - agentId:",
                  agentId,
                  "version:",
                  agentVersionAtRun,
                  "runNumber:",
                  runNumber,
                );

                if (!agentVersionAtRun || !runNumber) {
                  console.error("Missing version or run_number:", {
                    agentVersionAtRun,
                    runNumber,
                  });
                  return;
                }

                const artifactsData = await getAgentRunArtifacts(
                  agentId!,
                  agentVersionAtRun,
                  runNumber,
                );
                console.log("Artifacts API Response:", artifactsData);
                // Extract artifacts array from response
                const rawArtifacts = artifactsData?.artifacts || [];
                console.log("Raw artifacts:", rawArtifacts);

                // Transform artifacts to ensure they have all required fields
                // AND include run metadata for downloading
                const transformedArtifacts = rawArtifacts.map(
                  (art: any, index: number) => ({
                    id: art.id || `artifact-${runNumber}-${index}`,
                    name: art.name || art.filename || "Unknown",
                    filename: art.filename || art.name || "Unknown",
                    type: art.type || "",
                    url: art.url || "",
                    // Add run metadata for download
                    agent_id: agentId,
                    agent_version: agentVersionAtRun,
                    run_number: runNumber,
                  }),
                );

                console.log("Transformed artifacts:", transformedArtifacts);
                setArtifacts(transformedArtifacts);
                setLiveChatArtifacts(transformedArtifacts); // Also save as live chat artifacts
              } catch (err) {
                console.error("Failed to fetch artifacts:", err);
              }

              return;
            }

            // Error - stop polling and show error
            if (data.status === "error" || data.status === "failed") {
              console.error("Agent run failed:", data);
              stopPolling();
              setRunStatus({
                status: "error",
                message: data.message || "Agent execution failed",
                output: data.output,
              });
              return;
            }

            // Still pending - continue polling
            console.log("Still pending, polling again in 2s...");
          } catch (err) {
            console.error("Error polling result:", err);
          }

          // Schedule next poll
          pollTimeoutRef.current = setTimeout(pollResult, 2000);
        };

        // Start polling
        pollResult();
      }
    } catch (e) {
      setRunStatus({ status: "error", message: "Failed to run agent." });
      setSnackbarOpen(true);
      setHasRunTriggered(false);
    } finally {
    }
    setInputValue('');
  };

  const handlePinPrompt = async (visibility: any) => {
    const textToPin = inputValue.trim();
    if (!textToPin) {
      console.error("Cannot pin empty prompt");
      return;
    }
    try {
      const promptData = {
        prompt_text: textToPin,
        is_public: visibility?.toLowerCase() === "public",
      };

      const result = await createPinnedPromptForAgent(agentId, promptData);
      console.log("Prompt pinned successfully:", result);

      // // Refresh the pinned prompts list
      setPinnedPrompts((prev) => [...prev, result]);
    } catch (error) {
      console.error("Failed to pin prompt:", error);
    }
  };

  const getHelpText = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 0.5, // Space between hint and your textfield
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          Type&nbsp;
          <Box
            component="span"
            sx={{
              color: "#b87333",
              bgcolor: "rgba(184, 115, 51, 0.1)",
              px: 0.6,
              py: 0.1,
              borderRadius: 1,
              fontFamily: "monospace",
              fontWeight: "bold",
              mr: 0.5,
            }}
          >
            @file
          </Box>
          to reference files
        </Typography>
      </Box>
    );
  };

  const handleClearPrompt = () => {
    setInputValue("");
    setAttachedFiles([]);
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(finalResult);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const handleCopyHistoricalPrompt = () => {
    navigator.clipboard.writeText(historicalPrompt);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const handleThumbsUp = () => {
    setFeedbackState(feedbackState === "up" ? null : "up");
  };

  const handleThumbsDown = () => {
    setFeedbackState(feedbackState === "down" ? null : "down");
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEnhancePrompt = async () => {
    if (!inputValue.trim() || !agent) return;
    setIsEnhancing(true);
    setIsModalOpen(true);

    try {
      const result = await enhancePrompt({
        role: agent.role,
        description: agent.description,
        input_prompt: inputValue,
        tools: agent.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      setEnhancedSuggestion(result.enhanced_prompt);
    } catch (err) {
      console.error("Failed to enhance prompt:", err);
      setEnhancedSuggestion("Failed to generate enhancement");
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleDeletePinnedPrompt = async (promptId: string) => {
    try {
      await deletePinnedPromptForAgent(agentId!, promptId);
      setPinnedPrompts((prev) => prev.filter((p) => p.id !== promptId));
    } catch (error) {
      console.error("Failed to delete pinned prompt:", error);
    }
  };

  const handlePinnedPromptClick = (inputValue: string) => {
    setInputValue(inputValue);
  };

  const handleBackToLiveChat = () => {
    setSelectedRunHistory(null);
    setHistoricalPrompt("");
    setHistoricalResult("");

    // If there's an active run in progress, restore it
    if (isPolling || isRunning) {
      // Set flag to show "Still working" immediately
      setIsReturningToProgress(true);
      // Restore live chat artifacts for the active run
      setArtifacts(liveChatArtifacts);
    } else {
      // No active run - reset to fresh state (welcome screen)
      setHasRunTriggered(false);
      setFinalResult("");
      setIsPolling(false);
      setRunStatus(null);
      setArtifacts([]);
      setInputValue("");
      setSubmittedPrompt(""); // Clear the submitted prompt bubble
      setIsPromptExpanded(false); // Reset expand state
      setSelectedFiles([]);
      setIsReturningToProgress(false);
    }
  };

  return (
    <Box
      sx={{
        height: "87vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 2,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h4">
            {agent?.display_name || "Agent Run"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>
      {/* Container for Sidebars + Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          height: "95%",
          overflow: "hidden",
          mt: 2,
          position: "relative", // Required for the absolute positioning of the expand button
        }}
      >
        {/* History Sidebar */}
        <HistorySidebar
          isOpen={isHistoryOpen}
          agentRuns={agentRuns}
          selectedRunNumber={selectedRunHistory?.run_number || null}
          hasMoreHistory={hasMoreHistory}
          isLoadingMoreHistory={isLoadingMoreHistory}
          totalHistoryRuns={totalHistoryRuns}
          onToggleOpen={() => setIsHistoryOpen(true)}
          onToggleClose={() => setIsHistoryOpen(false)}
          onSelectRun={(run) => {
            setSelectedRunHistory(run);
            setSelectedRunId(run.id);
          }}
          onLoadMore={handleLoadMoreHistory}
        />

        {/* MAIN CONTENT AREA middle + right sidebar */}
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            flexDirection: "row-reverse",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <ArtifactsAndPinnedPromptsSideBar
            prompts={pinnedPrompts}
            artifacts={artifacts}
            onCopy={handleCopyPrompt}
            onDelete={handleDeletePrompt}
            onArtifactClick={handleArtifactDownload}
            deleting={deleting}
            error={error}
          />
          {/* MAIN CONTENT - Middle section : Prompt + Response */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              position: "relative",
              overflow: "hidden", // Changed from "auto" to "hidden" - children will handle scrolling
              bgcolor: "#FFFDFC",
            }}
          >
            {/* Scrollable Content Area */}
            <Box
              className="custom-scrollbar"
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                overflowX: "hidden",
                /* 1. MODERN SCROLLBAR LOGIC */
                scrollbarGutter: "stable",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "#b06d50" }, // Your copper shade
                },
                px: { xs: 2, md: 3 },
                pt: 4,
                pb: 2,
                bgcolor: "#FFFDFC",
              }}
            >
              {/* Run History View */}
              {selectedRunHistory ? (
                <Box
                  sx={{ flexGrow: 1, maxWidth: "100%", overflow: "visible" }}
                >
                  {/* Back to Live Chat Button */}
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Button
                      startIcon={<ArrowBackIcon />}
                      onClick={handleBackToLiveChat}
                      variant="outlined"
                      size="small"
                    >
                      Back to Live Chat
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Viewing Run #{selectedRunHistory.run_number} (Version{" "}
                      {selectedRunHistory.agent_version})
                    </Typography>
                  </Box>

                  {isLoadingHistorical ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "200px",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      {/* Historical Prompt */}
                      <Box sx={{ mb: 4 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                            px: 2,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, color: "text.secondary" }}
                          >
                            Prompt
                          </Typography>
                          <Tooltip title="Copy prompt">
                            <IconButton
                              size="small"
                              onClick={handleCopyHistoricalPrompt}
                              sx={{ opacity: 0.7 }}
                            >
                              <CopyIcon sx={{ fontSize: "1.2rem" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            px: 2,
                            py: 1.5,
                            ml: 1,
                            borderRadius: "16px",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        >
                          <Typography variant="body1">
                            {historicalPrompt}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Historical Result */}
                      <Box
                        sx={{ my: 1, px: 2, borderRadius: 1, maxWidth: "100%" }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, mb: 2 }}
                        >
                          Response
                        </Typography>

                        <Box
                          sx={{
                            lineHeight: 1.7,
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            maxWidth: "100%",
                            "& img": { maxWidth: "100%", height: "auto" },
                            "& pre": {
                              overflow: "auto",
                              maxWidth: "100%",
                            },
                            "& code": {
                              wordBreak: "break-all",
                            },
                          }}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: marked.parse(historicalResult),
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <>
                  {/* LIVE CHAT VIEW */}
                  {/* Wrapper for centered layout */}
                  <Box
                    sx={{
                      maxWidth: "900px", // Centered max width for chat
                      mx: "auto", // Center the content
                      width: "100%",
                    }}
                  >
                    {/* Contextual prompt bubble shows after user input */}
                    {(isPolling || finalResult) && (
                      <Box
                        sx={{
                          mb: 4,
                          display: "flex",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            px: 2,
                            py: 0.75,
                            borderRadius: "16px",
                            maxWidth: "600px",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            cursor: submittedPrompt.length > 90 ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (submittedPrompt.length > 90) {
                              setIsPromptExpanded(!isPromptExpanded);
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              flexGrow: 1,
                              wordBreak: "break-word",
                            }}
                          >
                            {isPromptExpanded || submittedPrompt.length <= 90
                              ? submittedPrompt
                              : `${submittedPrompt.substring(0, 90)}...`}
                          </Typography>
                          {submittedPrompt.length > 90 && (
                            <IconButton
                              size="small"
                              sx={{
                                padding: 0.5,
                                color: "text.secondary",
                                transition: "transform 0.2s",
                                transform: isPromptExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Welcome Message with Centered Input - Only show when no conversation yet and not viewing history */}
                    {!isPolling &&
                      !finalResult &&
                      !hasRunTriggered &&
                      !selectedRunHistory && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            minHeight: "300px",
                            gap: 4,
                            px: 4,
                          }}
                        >
                          {/* Welcome Message */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Avatar
                              variant="rounded"
                              src="/nextmoca-logo.png"
                              sx={{
                                bgcolor: "black",
                                width: 45,
                                height: 45,
                                borderRadius: 99,
                              }}
                              imgProps={{
                                sx: {
                                  objectFit: "cover",
                                },
                              }}
                            />
                            <Typography
                              variant="h5"
                              sx={{
                                color: "text.secondary",
                                fontWeight: 400,
                                textAlign: "center",
                              }}
                            >
                              Ask a question or paste from pinned prompts
                            </Typography>
                          </Box>

                          {/* Centered Input Field */}
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "700px",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                pr: 0.9,
                                mb: 0.5,
                              }}
                            >
                              {getHelpText()}
                            </Box>
                            <Box
                              ref={inputRef}
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <TextField
                                multiline
                                minRows={2}
                                maxRows={6}
                                fullWidth
                                placeholder="Ask a question or paste from pinned prompts... type @file to reference files"
                                value={inputValue}
                                onChange={handleInputChange}
                                variant="outlined"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={{
                                        position: "absolute",
                                        bottom: "9%",
                                        right: "6%",
                                        gap: 4.5,
                                        alignItems: "center",
                                      }}
                                    >
                                      <PinPromptDropdown
                                        onPin={handlePinPrompt}
                                        disabled={!inputValue.trim()}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={handleEnhanceClick}
                                        sx={{ color: "primary.main" }}
                                      >
                                        {isEnhancing ? (
                                          "..."
                                        ) : (
                                          <AutoFixHighIcon />
                                        )}
                                      </IconButton>
                                      {inputValue.trim().length > 0 ? (
                                        <IconButton
                                          onClick={handleRun}
                                          disabled={
                                            isRunning ||
                                            isPolling ||
                                            !inputValue.trim()
                                          }
                                          sx={{
                                            backgroundColor: "primary.main",
                                            borderRadius: "99px",
                                            width: 42,
                                            height: 42,
                                            color: "white",
                                            "&:hover": {
                                              backgroundColor: "primary.dark",
                                            },
                                          }}
                                        >
                                          <ArrowUpwardIcon
                                            sx={{
                                              stroke: "white",
                                              strokeWidth: 1.2,
                                            }}
                                          />
                                        </IconButton>
                                      ) : (
                                        /* Show Mic Icon if input is empty */
                                        <Tooltip title={isListening ? "Stop Listening" : "Voice Search"}>
                                          <IconButton
                                            onClick={handleMicClick}
                                            color={
                                              isListening ? "error" : "default"
                                            }
                                            sx={{
                                              animation: isListening
                                                ? "pulse 1.5s infinite"
                                                : "none",
                                              "@keyframes pulse": {
                                                "0%": { opacity: 1 },
                                                "50%": { opacity: 0.4 },
                                                "100%": { opacity: 1 },
                                              },
                                            }}
                                          >
                                            {isListening ? <StopIcon /> : <MicIcon />}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  width: "100%",
                                  minWidth: 0,
                                  "& .MuiOutlinedInput-root": {
                                    width: "100%",
                                    minWidth: 0,
                                    maxWidth: "100%",
                                    boxSizing: "border-box",
                                    borderRadius: "24px",
                                    paddingBottom: "54px",
                                    bgcolor: "white",
                                    "& fieldset": {
                                      borderColor: "rgba(0, 0, 0, 0.36)",
                                      borderWidth: "1.5px",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "rgba(0, 0, 0, 0.54)",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: "primary.main",
                                      borderWidth: "2px",
                                    },
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: "16px 14px",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    whiteSpace: "pre-wrap",
                                    "&::-webkit-scrollbar": { width: "9px" },
                                    "&::-webkit-scrollbar-thumb": {
                                      backgroundColor: "rgba(0,0,0,0.1)",
                                      borderRadius: "10px",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                      backgroundColor: "rgba(0,0,0,0.2)",
                                    },
                                  },
                                  "& textarea": {
                                    width: "100%",
                                    maxWidth: "100%",
                                    minWidth: 0,
                                    boxSizing: "border-box",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                  },
                                }}
                              />
                            </Box>

                            <Popper
                              open={Boolean(anchorEl)}
                              anchorEl={anchorEl}
                              placement="bottom-start"
                              style={{ zIndex: 1300 }}
                              modifiers={[
                                {
                                  name: "offset",
                                  options: { offset: [0, 10] },
                                },
                              ]}
                            >
                              <ClickAwayListener
                                onClickAway={() => setAnchorEl(null)}
                              >
                                <Paper
                                  sx={{
                                    mt: 1,
                                    border: "1px solid #444",
                                    minWidth: 360,
                                    maxHeight: 450,
                                    overflowY: "auto",
                                  }}
                                >
                                  <List dense>
                                    {isLoadingArtifacts ? (
                                      <ListItem>
                                        <ListItemText
                                          primary="Loading artifacts..."
                                          sx={{
                                            color: "#888",
                                            textAlign: "center",
                                          }}
                                        />
                                      </ListItem>
                                    ) : filteredOptions.length > 0 ? (
                                      filteredOptions.map((file) => (
                                        <ListItem
                                          disablePadding
                                          key={file.id}
                                          divider
                                          sx={{ borderColor: "#444" }}
                                        >
                                          <ListItemButton
                                            onClick={() =>
                                              handleFileSelect(file)
                                            }
                                          >
                                            <Avatar
                                              variant="rounded"
                                              src={file.thumbnail}
                                              sx={{
                                                bgcolor: "black",
                                                width: 32,
                                                height: 32,
                                                mr: 1.5,
                                                borderRadius: 1,
                                                border: file.thumbnail
                                                  ? "1px solid #e0e0e0"
                                                  : "none",
                                              }}
                                              imgProps={{
                                                sx: {
                                                  objectFit: "cover",
                                                },
                                              }}
                                            >
                                              <InsertDriveFileIcon
                                                sx={{
                                                  fontSize: 18,
                                                  color: "#fff",
                                                }}
                                              />
                                            </Avatar>
                                            <ListItemText
                                              primary={file.name}
                                              primaryTypographyProps={{
                                                fontWeight: "medium",
                                              }}
                                              secondary={`Version v${file.version} • Run ${file.runNumber}`}
                                            />
                                          </ListItemButton>
                                        </ListItem>
                                      ))
                                    ) : (
                                      <ListItem>
                                        <ListItemText
                                          primary="No matching files"
                                          sx={{
                                            color: "#888",
                                            textAlign: "center",
                                          }}
                                        />
                                      </ListItem>
                                    )}

                                    {hasLoadedArtifacts &&
                                      !isLoadingArtifacts && (
                                        <ListItem
                                          sx={{
                                            pt: 1,
                                            pb: 1,
                                            bgcolor: "#f5f5f5",
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: "#666" }}
                                                >
                                                  Showing {fileLibrary.length}{" "}
                                                  files from {loadedRunsCount}{" "}
                                                  most recent runs (out of{" "}
                                                  {totalRunsCount} total)
                                                </Typography>
                                                {hasMoreRuns && (
                                                  <Button
                                                    size="small"
                                                    onClick={loadMoreArtifacts}
                                                    sx={{ ml: 1 }}
                                                  >
                                                    Load More
                                                  </Button>
                                                )}
                                              </Box>
                                            }
                                          />
                                        </ListItem>
                                      )}
                                  </List>
                                </Paper>
                              </ClickAwayListener>
                            </Popper>

                            {/* Visual Badges for JSON Tracking - Centered Version */}
                            {(() => {
                              const includedFiles = selectedFiles.filter((f) =>
                                inputValue.includes(f.name),
                              );
                              const maxVisible = 3;
                              const visibleFiles = includedFiles.slice(
                                0,
                                maxVisible,
                              );
                              const remainingCount =
                                includedFiles.length - maxVisible;

                              return includedFiles.length > 0 ? (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    pt: 0.5,
                                    pb: 1.5,
                                    px: 1.5,
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    bgcolor: "rgba(0, 0, 0, 0.02)",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#666",
                                      fontWeight: 500,
                                      mb: 0.5,
                                      display: "block",
                                    }}
                                  >
                                    Files included in the prompt
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                  >
                                    {visibleFiles.map((file) => (
                                      <Chip
                                        key={file.id}
                                        avatar={
                                          file.thumbnail ? (
                                            <Avatar
                                              src={file.thumbnail}
                                              sx={{
                                                bgcolor: "white",
                                                width: 24,
                                                height: 24,
                                              }}
                                            />
                                          ) : (
                                            <Avatar
                                              sx={{
                                                bgcolor: "primary.main",
                                                width: 24,
                                                height: 24,
                                              }}
                                            >
                                              <InsertDriveFileIcon
                                                sx={{
                                                  fontSize: 14,
                                                  color: "white",
                                                }}
                                              />
                                            </Avatar>
                                          )
                                        }
                                        label={file.name}
                                        size="small"
                                        sx={{
                                          bgcolor: "white",
                                          border: "1px solid #e0e0e0",
                                          "& .MuiChip-label": {
                                            fontSize: "0.75rem",
                                          },
                                        }}
                                      />
                                    ))}
                                    {remainingCount > 0 && (
                                      <Chip
                                        label={`+${remainingCount} more`}
                                        size="small"
                                        sx={{
                                          bgcolor: "rgba(176, 109, 80, 0.1)",
                                          color: "primary.main",
                                          border:
                                            "1px solid rgba(176, 109, 80, 0.3)",
                                          "& .MuiChip-label": {
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                          },
                                        }}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              ) : null;
                            })()}
                          </Box>
                        </Box>
                      )}

                    {/* Sparkles Thinking Indicator */}
                    {isPolling && (
                      <ThinkingIndicator
                        thinkingDots={thinkingDots}
                        isReturningToProgress={isReturningToProgress}
                      />
                    )}

                    {!isPolling && finalResult && (
                      <AgentResponseDisplay
                        finalResult={finalResult}
                        feedbackState={feedbackState}
                        onCopy={handleCopyResult}
                        onThumbsUp={handleThumbsUp}
                        onThumbsDown={handleThumbsDown}
                        onRetry={handleRun}
                      />
                    )}
                    {runStatus && runStatus.output && (
                      <Accordion
                        sx={{ mt: 2, borderRadius: 1, boxShadow: "none" }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            Output (raw agent output)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box
                            component="pre"
                            sx={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              m: 0,
                            }}
                          >
                            {runStatus.output}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {runStatus &&
                      runStatus.status === "error" &&
                      (runStatus.message || runStatus.output) && (
                        <Accordion
                          sx={{
                            mt: 2,
                            background: "#ffebee",
                            borderRadius: 1,
                            boxShadow: "none",
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#b71c1c" }}
                            >
                              Error (click to expand)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box
                              component="pre"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                m: 0,
                                color: "#b71c1c",
                              }}
                            >
                              {runStatus.message}
                              {runStatus.output &&
                                (typeof runStatus.output === "string"
                                  ? `\n${runStatus.output}`
                                  : "")}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                  </Box>
                  {/* End of Gemini-style centered wrapper */}
                </>
              )}
            </Box>
            {/* End of scrollable content area */}

            {/* Chat Input Area - Fixed at Bottom - Only show after first run triggered */}
            {!selectedRunHistory && hasRunTriggered && (
              <Box
                sx={{
                  pt: 2,
                  pb: 3,
                  px: { xs: 2, md: 3 },
                  bgcolor: "#FFFDFC",
                  // borderTop: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    pr: 0.9,
                  }}
                >
                  {getHelpText()}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    bgcolor: "white",
                    width: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    ref={inputRef}
                    sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}
                  >
                    <TextField
                      multiline
                      minRows={2}
                      maxRows={6}
                      fullWidth
                      placeholder="Ask a question or paste from pinned prompts... type @file to reference files"
                      value={inputValue}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={{
                              position: "absolute",
                              bottom: "9%", // Fixed at bottom regardless of row count
                              right: "6%",
                              gap: 4.5,
                              alignItems: "center",
                            }}
                          >
                            <PinPromptDropdown
                              onPin={handlePinPrompt}
                              disabled={!inputValue.trim()}
                            />
                            <IconButton
                              size="small"
                              onClick={handleEnhanceClick}
                              sx={{ color: "primary.main" }}
                              disabled={isEnhancing || isPolling}
                            >
                              {<AutoFixHighIcon />}
                            </IconButton>

                            {inputValue.trim().length > 0 ? (
                              /* Show Arrow Up Icon if input has text */
                              <IconButton
                                onClick={handleRun}
                                disabled={isRunning || isPolling || !inputValue.trim()}
                                sx={{
                                  backgroundColor: "primary.main",
                                  borderRadius: "99px",
                                  width: 42,
                                  height: 42,
                                  color: "white",
                                  "&:hover": {
                                    backgroundColor: "primary.dark",
                                  },
                                }}
                              >
                                <ArrowUpwardIcon
                                  sx={{ stroke: "white", strokeWidth: 1.2 }}
                                />
                              </IconButton>
                            ) : (
                              /* Show Mic Icon if input is empty */
                              <Tooltip title={isListening ? "Stop Listening" : "Voice Search"}>
                                <IconButton
                                  onClick={handleMicClick}
                                  color={isListening ? "error" : "default"}
                                  sx={{
                                    animation: isListening ? "pulse 1.5s infinite" : "none",
                                    "@keyframes pulse": {
                                      "0%": { opacity: 1 },
                                      "50%": { opacity: 0.4 },
                                      "100%": { opacity: 1 },
                                    },
                                  }}
                                >
                                  {isListening ? <StopIcon /> : <MicIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        "& .MuiOutlinedInput-root": {
                          width: "100%",
                          minWidth: 0,
                          maxWidth: "100%",
                          boxSizing: "border-box",
                          borderRadius: "24px", // More rounded Gemini-style corners
                          /* 4. PREVENT TEXT OVERLAP */
                          paddingBottom: "54px",
                          "& fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.36)", // Darker border
                            borderWidth: "1.5px",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.54)", // Darker on hover
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main", // Theme color when focused
                            borderWidth: "2px",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "16px 14px",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          whiteSpace: "pre-wrap",
                          /* CUSTOM SCROLLBAR ADDED HERE */
                          "&::-webkit-scrollbar": { width: "9px" },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.1)",
                            borderRadius: "10px",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "rgba(0,0,0,0.2)",
                          },
                        },
                        "& textarea": {
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          boxSizing: "border-box",
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        },
                      }}
                    />
                  </Box>

                  <Popper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                    modifiers={[
                      { name: "offset", options: { offset: [0, 10] } },
                    ]}
                  >
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                      <Paper
                        sx={{
                          mt: 1,
                          border: "1px solid #444",
                          minWidth: 360,
                          maxHeight: 450,
                          overflowY: "auto",
                        }}
                      >
                        <List dense>
                          {isLoadingArtifacts ? (
                            <ListItem>
                              <ListItemText
                                primary="Loading artifacts..."
                                sx={{ color: "#888", textAlign: "center" }}
                              />
                            </ListItem>
                          ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((file) => (
                              <ListItem
                                disablePadding
                                key={file.id}
                                divider
                                sx={{ borderColor: "#444" }}
                              >
                                <ListItemButton
                                  onClick={() => handleFileSelect(file)}
                                >
                                  <ListItemAvatar sx={{ minWidth: 50 }}>
                                    <Avatar
                                      variant="rounded"
                                      src={file.thumbnail}
                                      sx={{
                                        bgcolor: file.thumbnail
                                          ? "transparent"
                                          : "primary.main",
                                        width: 40,
                                        height: 40,
                                        border: file.thumbnail
                                          ? "1px solid #e0e0e0"
                                          : "none",
                                      }}
                                      imgProps={{
                                        sx: {
                                          objectFit: "cover",
                                        },
                                      }}
                                    >
                                      {!file.thumbnail && (
                                        <Description fontSize="small" />
                                      )}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={file.name}
                                    primaryTypographyProps={{
                                      fontWeight: "medium",
                                    }}
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        sx={{ color: "#888" }}
                                      >
                                        Version:{" "}
                                        <span style={{ color: "#8d6e63" }}>
                                          v{file.version}
                                        </span>{" "}
                                        • Run: #{file.runNumber}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText
                                primary="No previous artifacts found"
                                sx={{ color: "#888", textAlign: "center" }}
                              />
                            </ListItem>
                          )}

                          {/* Load More Button and Status */}
                          {hasLoadedArtifacts && !isLoadingArtifacts && (
                            <ListItem
                              sx={{
                                flexDirection: "column",
                                gap: 1,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: "#888", textAlign: "center" }}
                              >
                                Showing artifacts from {loadedRunsCount} of{" "}
                                {totalRunsCount} runs
                                {filteredOptions.length > 0 &&
                                  ` (${filteredOptions.length} files)`}
                              </Typography>
                              {hasMoreRuns && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadMoreArtifacts();
                                  }}
                                  sx={{ textTransform: "none" }}
                                >
                                  Load More Runs
                                </Button>
                              )}
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>

                  {/* Visual Badges for JSON Tracking */}
                  {(() => {
                    const includedFiles = selectedFiles.filter((f) =>
                      inputValue.includes(f.name),
                    );
                    const maxVisible = 3;
                    const visibleFiles = includedFiles.slice(0, maxVisible);
                    const remainingCount = includedFiles.length - maxVisible;

                    return includedFiles.length > 0 ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 0.5,
                          pb: 1.5,
                          px: 1.5,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          bgcolor: "rgba(0, 0, 0, 0.02)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#666",
                            fontWeight: 500,
                            mb: 0.5,
                            display: "block",
                          }}
                        >
                          Files included in the prompt
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {visibleFiles.map((file) => (
                            <Chip
                              key={file.id}
                              label={file.name}
                              size="small"
                              avatar={
                                file.thumbnail ? (
                                  <Avatar
                                    src={file.thumbnail}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      border: "1px solid #e0e0e0",
                                    }}
                                  />
                                ) : undefined
                              }
                              icon={
                                !file.thumbnail ? (
                                  <Description
                                    sx={{
                                      fontSize: "14px",
                                      color: "primary.main",
                                    }}
                                  />
                                ) : undefined
                              }
                              sx={{
                                bgcolor: "rgba(120,151,105,0.2)",
                                border: "1px solid #5d4037",
                              }}
                            />
                          ))}
                          {remainingCount > 0 && (
                            <Tooltip
                              title={
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Additional files:
                                  </Typography>
                                  {includedFiles
                                    .slice(maxVisible)
                                    .map((file) => (
                                      <Box
                                        key={file.id}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mt: 0.5,
                                        }}
                                      >
                                        {file.thumbnail ? (
                                          <Avatar
                                            src={file.thumbnail}
                                            sx={{
                                              width: 16,
                                              height: 16,
                                              mr: 0.5,
                                              border: "1px solid #e0e0e0",
                                            }}
                                          />
                                        ) : (
                                          <Description
                                            sx={{ fontSize: 16, mr: 0.5 }}
                                          />
                                        )}
                                        <Typography variant="caption">
                                          {file.name}
                                        </Typography>
                                      </Box>
                                    ))}
                                </Box>
                              }
                              arrow
                            >
                              <Chip
                                label={`+${remainingCount} more`}
                                size="small"
                                icon={
                                  <AttachFileIcon sx={{ fontSize: "14px" }} />
                                }
                                sx={{
                                  bgcolor: "rgba(120,151,105,0.15)",
                                  border: "1px dashed #5d4037",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              </Box>
            )}
            {/* End of sticky input */}
          </Box>
          <PromptEnhancerDialog
            agent={agentCard}
            originalPrompt={inputValue}
            enhancedPrompt={enhancedSuggestion}
            isEnhancing={isEnhancing}
            isOpen={isModalOpen}
            onAccept={handleAcceptEnhancement}
            onReject={handleRejectEnhancement}
            onRegenerate={regeneratePrompt}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AgentRunPage;
